import { useEffect, useState } from 'react';
import { Wallet, Lock, Unlock, FileText, Plus, ArrowDown, ArrowUp } from 'lucide-react';
import type {
  CorteCajaVista,
  ResumenDiaVista,
  MovimientoCajaVista,
  RegistrarMovimientoCajaInput,
  TipoMovimientoCaja,
} from '@shared/types';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
import { useSessionStore } from '@renderer/store/useSessionStore';
import { formatPrecio, formatFecha } from '@renderer/utils/formatters';
import { montoEnLetras } from '@shared/numeroALetras';
import { NumberField } from '@renderer/components/ui/NumberField';
import { HelpButton } from '@renderer/components/help/HelpButton';
import { tourCorteCaja } from '@renderer/tours';
import { ContadorBilletes } from '@renderer/components/corte/ContadorBilletes';
import { StepUpModal } from '@renderer/components/auth/StepUpModal';
import { useStepUp } from '@renderer/hooks/useStepUp';
import { traducirErrorBackend } from '@renderer/lib/mensajesError';
import { pesosACentavos, centavosAPesos } from '@shared/centavos';

const CATEGORIAS_EGRESO_UI = [
  { id: 'GASTO_PROVEEDOR', label: 'Gasto a proveedor' },
  { id: 'GASTO_OPERATIVO', label: 'Gasto operativo' },
  { id: 'ADELANTO_EMPLEADO', label: 'Adelanto a empleado' },
  { id: 'OTRO', label: 'Otro egreso' },
];
const CATEGORIAS_INGRESO_UI = [
  { id: 'INGRESO_EXTRA', label: 'Ingreso extra' },
  { id: 'APORTACION_DUENO', label: 'Aportación de la dueña' },
  { id: 'OTRO', label: 'Otro ingreso' },
];

/**
 * Sprint 141 — Lo que el servidor dice del corte abierto: el total esperado **y su desglose**,
 * todo del mismo periodo (desde que se abrió la caja).
 *
 * Antes esta pantalla mezclaba dos relojes: pedía el resumen de HOY y lo enseñaba al lado de un
 * total que acumulaba desde la apertura. Con la caja abierta una semana decía "ventas en efectivo
 * $0" y "cuenta $4,579" en el mismo recuadro.
 */
type EsperadoDelCorte = {
  corteId: number;
  montoInicial: number;
  totalEfectivo: number;
  totalPropinasEfectivo: number;
  totalIngresos: number;
  totalEgresos: number;
  efectivoEsperado: number;
  desde: string;
  totalTarjeta: number;
  totalTransferencia: number;
  totalVentas: number;
  totalPropinas: number;
  cantidadPedidosPagados: number;
  ticketPromedio: number;
};

// Sprint 24 — Prop `embebido` para reutilizar dentro del wizard "Cerrar Día"
// sin duplicar el header. Cero refactor: misma página sirve standalone y embebida.
export function CorteCajaPage({
  embebido = false,
  onCambio,
}: { embebido?: boolean; onCambio?: () => void } = {}) {
  const sesion = useSessionStore((s) => s.sesion);
  const negocio = useNegocioStore((s) => s.negocio);
  const [corteAbierto, setCorteAbierto] = useState<CorteCajaVista | null>(null);
  const [resumen, setResumen] = useState<ResumenDiaVista | null>(null);
  /**
   * Sprint 70 (DT-068) — El efectivo esperado lo calcula el servidor, con el MISMO código que
   * cierra el corte.
   *
   * Esta pantalla lo armaba por su cuenta a partir de `ventasDelDia()` sin argumentos —el día de
   * calendario— mientras el cierre usa el rango real de la caja. Con el negocio cerrando pasada
   * la medianoche los dos números no coincidían, y "corregir" el conteo para que cuadrara con la
   * pantalla grababa un faltante que no existía.
   */
  const [esperadoServidor, setEsperadoServidor] = useState<EsperadoDelCorte | null>(null);
  const [movimientos, setMovimientos] = useState<MovimientoCajaVista[]>([]);
  const [montoInicial, setMontoInicial] = useState<number>(0);
  const [observaciones, setObservaciones] = useState('');
  const [montoFinalDeclarado, setMontoFinalDeclarado] = useState<number | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);
  const [modalMovimiento, setModalMovimiento] = useState<TipoMovimientoCaja | null>(null);
  // Sprint 32 — step-up para cierre de caja.
  const stepUp = useStepUp<{ corte: CorteCajaVista; pdfPath: string }>();

  const monedaSimbolo = negocio?.monedaSimbolo ?? '$';
  const locale = negocio?.locale ?? 'es-MX';

  const cargar = async () => {
    setCargando(true);
    const [c, r, esp] = await Promise.all([
      window.api.corte.abierto(),
      // El resumen del día se sigue mostrando como informativo (ventas, tarjeta, propinas).
      window.api.reportes.ventasDelDia(),
      // Pero el número con el que se cuenta la gaveta viene del servidor.
      window.api.corte.esperado(),
    ]);
    if (c.ok) {
      setCorteAbierto(c.data);
      if (c.data) {
        const mv = await window.api.movimientosCaja.listarDelCorte(c.data.id);
        if (mv.ok) setMovimientos(mv.data);
      } else {
        setMovimientos([]);
      }
    }
    if (r.ok) setResumen(r.data);
    setEsperadoServidor(esp.ok ? esp.data : null);
    setCargando(false);
  };

  useEffect(() => { cargar(); }, []);

  const onAbrir = async () => {
    if (!sesion) return;
    // Sprint 30 — confirmación con monto en número y letras: red de seguridad
    // contra errores de dedo (ej. teclear 7500 en vez de 750).
    const ok = await window.confirmar({
      titulo: 'Confirma el monto inicial de la caja',
      mensaje:
        `Vas a abrir la caja con ${formatPrecio(montoInicial, monedaSimbolo, locale)}` +
        ` — ${montoEnLetras(centavosAPesos(montoInicial))}. Revísalo bien antes de continuar.`,
      textoConfirmar: 'Sí, es correcto',
      textoCancelar: 'No, corregir',
      variant: 'info',
    });
    if (!ok) return;
    setEnviando(true);
    setError(null);
    const res = await window.api.corte.abrir(montoInicial, sesion.usuarioId);
    setEnviando(false);
    if (res.ok) setCorteAbierto(res.data);
    else setError(traducirErrorBackend(res.error, res.codigo));
  };

  const onCerrar = async () => {
    if (!corteAbierto) return;

    // Sprint 12.1c — aviso si hay propinas pendientes (el efectivo en gaveta las incluye).
    const pz = await window.api.propinas.pozo();
    if (pz.ok && pz.data.totalPendiente > 0) {
      const monedaFmt = formatPrecio(pz.data.totalPendiente, monedaSimbolo, locale);
      const okPropinas = await window.confirmar({
        titulo: 'Tienes propinas sin repartir',
        mensaje: `Hay ${monedaFmt} en propinas pendientes. Recuerda que el efectivo en gaveta las incluye — al contar físicamente, debe coincidir con el esperado (que ya las suma). Puedes repartirlas hoy o cualquier otro día desde el menú "Propinas". ¿Continuar con el cierre?`,
        textoConfirmar: 'Sí, cerrar caja',
        textoCancelar: 'No, voy a repartir primero',
        variant: 'info',
      });
      if (!okPropinas) return;
    }

    const ok = await window.confirmar({
      titulo: '¿Cerrar la caja?',
      mensaje: montoFinalDeclarado === null
        ? 'No declaraste el efectivo físico final. Se cerrará la caja sin medir diferencia. ¿Continuar?'
        : 'Esta acción no se puede deshacer. Solo se cierra el corte de caja — el inventario y el día se cierran en pasos aparte.',
      textoConfirmar: 'Sí, cerrar caja',
      variant: 'warn',
    });
    if (!ok) return;
    setEnviando(true);
    setError(null);
    // Sprint 32 — envuelto en step-up.
    const res = await stepUp.ejecutar(() => window.api.corte.cerrar(corteAbierto.id, {
      montoFinalDeclarado: montoFinalDeclarado ?? undefined,
      observaciones: observaciones || undefined,
    }));
    setEnviando(false);
    if (res.ok) {
      const abrirRes = await window.api.ticket.abrirPdf(res.data.pdfPath);
      if (!abrirRes.ok) window.alertar({ titulo: 'PDF', mensaje: `Corte cerrado, pero no se pudo abrir el PDF: ${traducirErrorBackend(abrirRes.error, abrirRes.codigo)}`, variant: 'warn' });
      cargar();
      setObservaciones('');
      setMontoFinalDeclarado(null);
      // Sprint 43 — notificar al wizard padre para que actualice el chip "hecho".
      onCambio?.();
    } else if (res.codigo === 'CANCELADO') {
      setError(null);
    } else {
      setError(traducirErrorBackend(res.error, res.codigo));
    }
  };

  const totalIngresos = movimientos.filter((m) => m.tipo === 'INGRESO').reduce((s, m) => s + m.monto, 0);
  const totalEgresos = movimientos.filter((m) => m.tipo === 'EGRESO').reduce((s, m) => s + m.monto, 0);

  if (cargando) return <div className="text-center text-slate-400 py-8">Cargando...</div>;

  return (
    <div className="space-y-4">
      {!embebido && (
        <div className="flex items-center justify-between gap-3" data-tour="corte-header">
          <div className="flex items-center gap-3">
            <Wallet className="h-7 w-7 text-primary" />
            <div>
              <h1 className="text-2xl font-bold">Corte de Caja</h1>
              <p className="text-sm text-slate-500">
                {corteAbierto ? 'Día abierto. Cierra al final de la jornada.' : 'Sin caja abierta. Abre al inicio del día.'}
              </p>
            </div>
          </div>
          <HelpButton tour={tourCorteCaja} />
        </div>
      )}

      <div data-tour="corte-seccion-principal">
        {!corteAbierto ? (
          <AbrirCaja monto={montoInicial} setMonto={setMontoInicial} onAbrir={onAbrir} enviando={enviando} monedaSimbolo={monedaSimbolo} />
        ) : (
          <CerrarCaja
            corte={corteAbierto}
            resumen={resumen}
            esperadoServidor={esperadoServidor}
            movimientos={movimientos}
            totalIngresos={totalIngresos}
            totalEgresos={totalEgresos}
            observaciones={observaciones}
            setObservaciones={setObservaciones}
            montoFinalDeclarado={montoFinalDeclarado}
            setMontoFinalDeclarado={setMontoFinalDeclarado}
            onCerrar={onCerrar}
            enviando={enviando}
            monedaSimbolo={monedaSimbolo}
            locale={locale}
            onAbrirModalMovimiento={(tipo) => setModalMovimiento(tipo)}
          />
        )}
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
          {error}
        </div>
      )}

      {modalMovimiento && sesion && (
        <ModalRegistrarMovimiento
          tipo={modalMovimiento}
          monedaSimbolo={monedaSimbolo}
          usuarioId={sesion.usuarioId}
          onClose={() => setModalMovimiento(null)}
          onRegistrado={() => {
            setModalMovimiento(null);
            cargar();
          }}
        />
      )}

      {/* Sprint 32 — Step-up de 2FA para cierre de caja. */}
      <StepUpModal
        abierto={stepUp.modalAbierto}
        onConfirmado={stepUp.onConfirmado}
        onCancelar={stepUp.onCancelar}
      />
    </div>
  );
}

function AbrirCaja({
  monto, setMonto, onAbrir, enviando, monedaSimbolo,
}: { monto: number; setMonto: (v: number) => void; onAbrir: () => void; enviando: boolean; monedaSimbolo: string }) {
  return (
    <div className="card max-w-md mx-auto space-y-4">
      <div className="text-center">
        <Unlock className="h-12 w-12 text-green-500 mx-auto mb-2" />
        <h2 className="text-xl font-bold">Abrir caja</h2>
        <p className="text-sm text-slate-500">Registra el efectivo inicial con el que comienzas el día.</p>
      </div>
      <div>
        <label className="label" htmlFor="monto">Monto inicial ({monedaSimbolo})</label>
        <NumberField id="monto" min={0} decimals={2} className="input text-2xl font-mono text-right" value={centavosAPesos(monto)} onChange={(n) => setMonto(pesosACentavos(n))} />
        <p className="help-text">Puedes dejarlo en 0 si no tienes fondo de caja inicial.</p>
      </div>
      <button onClick={onAbrir} disabled={enviando} className="btn-primary btn-lg w-full">
        <Unlock className="h-5 w-5" /> Abrir caja
      </button>
    </div>
  );
}

function CerrarCaja(props: {
  corte: CorteCajaVista;
  resumen: ResumenDiaVista | null;
  /** Sprint 70 — El esperado que va a usar el cierre. `null` mientras carga. */
  esperadoServidor: EsperadoDelCorte | null;
  movimientos: MovimientoCajaVista[];
  totalIngresos: number;
  totalEgresos: number;
  observaciones: string;
  setObservaciones: (v: string) => void;
  montoFinalDeclarado: number | null;
  setMontoFinalDeclarado: (v: number | null) => void;
  onCerrar: () => void;
  enviando: boolean;
  monedaSimbolo: string;
  locale: string;
  onAbrirModalMovimiento: (tipo: TipoMovimientoCaja) => void;
}) {
  const {
    corte, resumen, esperadoServidor, movimientos, totalIngresos, totalEgresos, observaciones, setObservaciones,
    montoFinalDeclarado, setMontoFinalDeclarado, onCerrar, enviando, monedaSimbolo, locale, onAbrirModalMovimiento,
  } = props;
  if (!resumen) return null;

  // Sprint 12.1c — propinas en efectivo están físicamente en gaveta (RN-S12.1c-02).
  const totalPropinasEfectivo = resumen.totalPropinasEfectivo ?? 0;
  /**
   * Sprint 70 (DT-068) — Se usa el número del servidor. El cálculo local queda SÓLO como respaldo
   * para el instante en que la consulta todavía no respondió: no puede haber dos fórmulas
   * compitiendo por decirle a la dueña cuánto debería haber en la gaveta.
   */
  const efectivoEsperado =
    esperadoServidor?.efectivoEsperado ??
    corte.montoInicial + resumen.totalEfectivo + totalPropinasEfectivo + totalIngresos - totalEgresos;

  /**
   * Sprint 141 — Todo lo que se enseña en esta pantalla sale del **mismo periodo**.
   *
   * Mientras la consulta no vuelve, se cae al resumen del día: es lo único que hay a mano y dura
   * un parpadeo. Lo que ya no puede pasar es que una parte diga "hoy" y otra "desde que abriste"
   * de forma permanente, que es lo que hacía.
   */
  const datosCorte = esperadoServidor ?? {
    totalEfectivo: resumen.totalEfectivo,
    totalTarjeta: resumen.totalTarjeta,
    totalTransferencia: resumen.totalTransferencia,
    totalVentas: resumen.totalVentas,
    cantidadPedidosPagados: resumen.cantidadPedidosPagados,
    ticketPromedio: resumen.ticketPromedio,
  };
  const diferencia = montoFinalDeclarado !== null
    ? Math.round((montoFinalDeclarado - efectivoEsperado) * 100) / 100
    : null;

  return (
    <div className="space-y-4">
      <div className="card">
        <div className="text-center">
          <p className="text-xs text-slate-500">Caja abierta desde</p>
          <p className="text-lg font-semibold">{formatFecha(corte.fechaApertura, locale)}</p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="card space-y-2">
          {/*
            Sprint 141 — Dice DESDE CUÁNDO cuenta, y cuenta lo mismo que el efectivo esperado.

            Se llamaba "Resumen del día" y mostraba hoy, aunque la caja llevara una semana
            abierta: quien cierra compara estos números con el efectivo de la gaveta, así que
            tienen que hablar del mismo rato.
          */}
          <h3 className="font-semibold mb-1">Desde que abriste la caja</h3>
          <p className="mb-1 text-xs text-slate-500">{formatFecha(corte.fechaApertura, locale)}</p>
          <ResumenLinea label="Pedidos cobrados" valor={String(datosCorte.cantidadPedidosPagados)} />
          <ResumenLinea label="Total ventas" valor={formatPrecio(datosCorte.totalVentas, monedaSimbolo, locale)} bold />
          <ResumenLinea label="Ticket promedio" valor={formatPrecio(datosCorte.ticketPromedio, monedaSimbolo, locale)} />
        </div>
        <div className="card space-y-2">
          <h3 className="font-semibold mb-1">Por método de pago</h3>
          <ResumenLinea label="Efectivo" valor={formatPrecio(datosCorte.totalEfectivo, monedaSimbolo, locale)} />
          <ResumenLinea label="Tarjeta" valor={formatPrecio(datosCorte.totalTarjeta, monedaSimbolo, locale)} />
          <ResumenLinea label="Transferencia" valor={formatPrecio(datosCorte.totalTransferencia, monedaSimbolo, locale)} />
        </div>
      </div>

      {/* Sprint 12.1b — Movimientos de caja */}
      <div className="card space-y-2" data-tour="corte-movimientos">
        <div className="flex justify-between items-center">
          <h3 className="font-semibold">Movimientos de caja</h3>
          <div className="flex gap-2">
            <button onClick={() => onAbrirModalMovimiento('EGRESO')} className="btn-ghost text-xs flex items-center gap-1 text-red-600">
              <ArrowDown className="h-3 w-3" /> Egreso
            </button>
            <button onClick={() => onAbrirModalMovimiento('INGRESO')} className="btn-ghost text-xs flex items-center gap-1 text-green-600">
              <ArrowUp className="h-3 w-3" /> Ingreso
            </button>
          </div>
        </div>
        {movimientos.length === 0 ? (
          <p className="text-xs text-slate-500">Sin movimientos extra registrados.</p>
        ) : (
          <ul className="divide-y divide-slate-100 text-sm">
            {movimientos.map((m) => (
              <li key={m.id} className="py-1.5 flex justify-between gap-2">
                <div>
                  <span className={m.tipo === 'EGRESO' ? 'text-red-600' : 'text-green-700'}>
                    {m.tipo === 'EGRESO' ? '↓' : '↑'} {m.categoria.replace('_', ' ')}
                  </span>
                  <span className="block text-xs text-slate-500 truncate max-w-[220px]" title={m.justificacion}>
                    {m.justificacion}
                  </span>
                </div>
                <span className={`font-mono font-semibold ${m.tipo === 'EGRESO' ? 'text-red-700' : 'text-green-700'}`}>
                  {m.tipo === 'EGRESO' ? '-' : '+'}{formatPrecio(m.monto, monedaSimbolo, locale)}
                </span>
              </li>
            ))}
            <li className="pt-1.5 flex justify-between text-xs">
              <span className="text-slate-500">Total ingresos / egresos</span>
              <span className="font-mono">
                +{formatPrecio(totalIngresos, monedaSimbolo, locale)} / -{formatPrecio(totalEgresos, monedaSimbolo, locale)}
              </span>
            </li>
          </ul>
        )}
      </div>

      <div className="card bg-amber-50 border-amber-200">
        <h3 className="font-semibold mb-2">Efectivo en caja esperado</h3>
        <ResumenLinea label="Monto inicial" valor={formatPrecio(corte.montoInicial, monedaSimbolo, locale)} />
        {/*
          Sprint 141 — Este renglón viene del servidor, igual que el total de abajo.

          Antes salía de `reportes.ventasDelDia()` sin argumentos —o sea, HOY— mientras el total
          acumula desde que se abrió la caja. Con la caja abierta una semana, la pantalla decía
          "ventas en efectivo $0" y a renglón seguido "cuenta $4,579". Dos verdades que juntas no
          se pueden explicar, en la pantalla donde un POS se gana la confianza del negocio.
        */}
        <ResumenLinea
          label="+ Ventas en efectivo"
          valor={formatPrecio(datosCorte.totalEfectivo, monedaSimbolo, locale)}
        />
        {totalPropinasEfectivo > 0 && (
          <ResumenLinea
            label="+ Propinas en efectivo"
            valor={formatPrecio(totalPropinasEfectivo, monedaSimbolo, locale)}
          />
        )}
        {totalIngresos > 0 && <ResumenLinea label="+ Ingresos a caja" valor={formatPrecio(totalIngresos, monedaSimbolo, locale)} />}
        {totalEgresos > 0 && <ResumenLinea label="- Egresos de caja" valor={formatPrecio(totalEgresos, monedaSimbolo, locale)} />}
        <div className="mt-2 border-t border-amber-300 pt-2 flex justify-between items-baseline">
          <span className="font-bold">= Total esperado</span>
          <span className="text-xl font-bold font-mono">{formatPrecio(efectivoEsperado, monedaSimbolo, locale)}</span>
        </div>
        {totalPropinasEfectivo > 0 && (
          <p className="text-xs text-amber-800 mt-2">
            ℹ️ Las propinas en efectivo ya están sumadas porque están físicamente en la gaveta.
            Cuenta TODO el efectivo que tengas (incluyendo propinas) y compara contra el esperado.
          </p>
        )}
      </div>

      {/* Sprint 12 + 20 — Conteo físico con wizard de billetes (reduce error humano). */}
      <div className="card border-2 border-primary/20 space-y-3" data-tour="corte-contador-billetes">
        <div>
          <label className="label">
            Efectivo contado físicamente en caja ({monedaSimbolo})
            <span className="text-xs text-slate-500 ml-2 font-normal">(opcional pero recomendado)</span>
          </label>
          <p className="text-xs text-slate-500">
            Cuenta billete por billete: el total se calcula solo. Reduce errores de suma.
          </p>
        </div>
        <ContadorBilletes
          monedaSimbolo={monedaSimbolo}
          value={montoFinalDeclarado ?? 0}
          onChange={(v) => setMontoFinalDeclarado(v > 0 ? v : null)}
        />
        {diferencia !== null && (
          <div className={`rounded-lg p-3 text-center border ${
            diferencia === 0 ? 'bg-green-50 border-green-200 text-green-800'
              : diferencia > 0 ? 'bg-blue-50 border-blue-200 text-blue-800'
              : 'bg-rose-50 border-rose-200 text-rose-800'
          }`}>
            <div className="text-xs uppercase font-medium">
              {diferencia === 0 ? '✓ La caja CUADRA' : diferencia > 0 ? 'SOBRANTE detectado' : 'FALTANTE detectado'}
            </div>
            <div className="text-xl font-mono font-bold">
              {diferencia >= 0 ? '+' : ''}{formatPrecio(diferencia, monedaSimbolo, locale)}
            </div>
          </div>
        )}
      </div>

      <div className="card">
        <label className="label" htmlFor="obs">Observaciones (opcional)</label>
        <textarea
          id="obs" className="input" rows={2} value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)}
          placeholder="Diferencias, notas del día, eventos, etc."
        />
      </div>

      <button onClick={onCerrar} disabled={enviando} className="btn-danger btn-lg w-full">
        <Lock className="h-5 w-5" />
        {enviando ? 'Cerrando caja…' : 'Cerrar caja y generar PDF'}
      </button>
      <p className="text-center text-xs text-slate-500">
        <FileText className="inline h-3 w-3" /> Se generará un PDF con el resumen del corte de caja.
        El cierre del inventario y del día se hacen en los siguientes pasos.
      </p>
    </div>
  );
}

function ModalRegistrarMovimiento(props: {
  tipo: TipoMovimientoCaja;
  monedaSimbolo: string;
  usuarioId: number;
  onClose: () => void;
  onRegistrado: () => void;
}) {
  const { tipo, monedaSimbolo, usuarioId, onClose, onRegistrado } = props;
  const categorias = tipo === 'EGRESO' ? CATEGORIAS_EGRESO_UI : CATEGORIAS_INGRESO_UI;
  const [categoria, setCategoria] = useState<string>(categorias[0].id);
  const [monto, setMonto] = useState<number>(0);
  const [justificacion, setJustificacion] = useState<string>('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    if (monto <= 0) { setError('Monto debe ser mayor a 0'); return; }
    if (justificacion.trim().length < 5) { setError('Justificación: mínimo 5 caracteres'); return; }
    setEnviando(true);
    const data: RegistrarMovimientoCajaInput = {
      tipo, categoria, monto, justificacion: justificacion.trim(), usuarioId,
    };
    const r = await window.api.movimientosCaja.registrar(data);
    setEnviando(false);
    if (r.ok) onRegistrado();
    else setError(traducirErrorBackend(r.error, r.codigo));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl max-h-[90vh] flex flex-col">
        <div className="shrink-0 border-b px-5 py-3 flex items-center gap-2">
          {tipo === 'EGRESO' ? <ArrowDown className="h-5 w-5 text-red-600" /> : <ArrowUp className="h-5 w-5 text-green-600" />}
          <h2 className="font-semibold">Registrar {tipo === 'EGRESO' ? 'egreso' : 'ingreso'} de caja</h2>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          <div>
            <label className="label">Categoría</label>
            <select className="input" value={categoria} onChange={(e) => setCategoria(e.target.value)}>
              {categorias.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Monto ({monedaSimbolo})</label>
            <NumberField min={0} decimals={2} className="input text-xl font-mono text-right" value={centavosAPesos(monto)} onChange={(n) => setMonto(pesosACentavos(n))} />
          </div>
          <div>
            <label className="label">Justificación <span className="text-red-600">*</span></label>
            <textarea
              className="input" rows={3}
              value={justificacion}
              onChange={(e) => setJustificacion(e.target.value)}
              placeholder={tipo === 'EGRESO' ? 'Ej: Compra de bolsas plásticas con Don Pepe' : 'Ej: Aportación dueña para fondo'}
              maxLength={500}
            />
            <p className="help-text">Mínimo 5 caracteres. Quedará en bitácora del corte.</p>
          </div>
          {error && <div className="rounded bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">{error}</div>}
        </div>
        <div className="shrink-0 border-t px-5 py-3 flex gap-2 justify-end bg-slate-50 rounded-b-xl">
          <button onClick={onClose} className="btn-ghost" disabled={enviando}>Cancelar</button>
          <button onClick={submit} className="btn-primary" disabled={enviando}>
            <Plus className="h-4 w-4" /> {enviando ? 'Registrando...' : 'Registrar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ResumenLinea({ label, valor, bold }: { label: string; valor: string; bold?: boolean }) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-slate-600">{label}</span>
      <span className={`font-mono ${bold ? 'font-bold' : ''}`}>{valor}</span>
    </div>
  );
}
